import { useState, type ReactNode } from 'react'
import { EmptyState, ErrorState, Skeleton } from '../../ui/Feedback'
import { Button } from '../../ui/Button'
import { ApiError } from '../../core/errors'

export interface QueryLike<T> {
  data: T | undefined
  error: unknown
  isLoading: boolean
  isFetching?: boolean
  isStale?: boolean
  refetch: () => Promise<T | undefined>
}

export function DataGuard<T>({ query, children, empty, loading = <div className="skeleton-stack"><Skeleton /><Skeleton /><Skeleton /></div> }: { query: QueryLike<T>; children: (data: T) => ReactNode; empty: { title: string; body: string; action?: ReactNode; when?: (data: T) => boolean }; loading?: ReactNode }) {
  const [retrying, setRetrying] = useState(false)
  const retry = () => {
    if (retrying) return
    setRetrying(true)
    void query.refetch().finally(() => setRetrying(false))
  }
  if (query.isLoading) return <>{loading}</>
  if (query.error && !query.data) return <ErrorState body={query.error instanceof ApiError ? query.error.explanation : query.error instanceof Error ? 'The console could not refresh this data. Try again in a moment.' : 'The service returned no explanation.'} retrying={retrying} onRetry={retry} />
  if (query.data === undefined) return null
  return <>{query.error && <div className="stale-strip" role="status">Showing the last good result · refresh failed <Button size="sm" loading={retrying} disabled={retrying} onClick={retry}>Try again</Button></div>}{(Array.isArray(query.data) && query.data.length === 0) || empty.when?.(query.data) ? <EmptyState title={empty.title} body={empty.body} action={empty.action} /> : children(query.data)}</>
}
